<template>
  <div>
    <EmailRecipientsDialog
      v-if="showRecipientsDialog"
      v-model="showRecipientsDialog"
      :portcallId="portcallId"
      :location="location"
      :client-id="clientId"
      :vessel-id="vesselId"
      :proforma-id="proformaId"
      @recipients-selected="recipientsSelected"
      @cancel="recipientsCancelled"
      :redirect="false"
      :to="emailTo"
      :cc="emailCc"
      :bcc="emailBcc" />
    <email-status
      :showForm="showEmailStatusDialog"
      :guid="emailTemplateGuid"
      @close="close()"
      @documentReady="documentReady($event)"
      @goToEmailClicked="goToEmailClicked($event)"
      @documentCreated="documentCreated($event)"
      :noAction="noAction" />
  </div>
</template>

<script>
import { apiGet, apiDelete } from '@/store/api';
import { usePortcallStore } from '@/store/portcallStore';
import { isValidEmail } from '@/modules/email/emailFunctions';
import EmailStatus from '@/app-components/EmailStatus.vue';
import EmailRecipientsDialog from '@/modules/email/EmailRecipientsDialog.vue';
import { useApplicationStore } from '@/store/applicationStore';
import { getMailAttachments } from '@/services/api/api-calls/getMailAttachments';
import SednaApiService from '@/services/api/external/sednaAPI';
import getLocationDetail from '../services/helpers/getLocationDetail';

export default {
  name: 'EmailGenerator',
  props: {
    location: String,
    portcallId: [String, Number],
    vesselId: {
      type: [Number, String],
      required: false,
    },
    proformaId: {
      type: [Number, String],
      required: false,
    },
    clientId: {
      type: [Number, String],
      required: false,
    },
    guid: {
      type: String,
      default: undefined,
    },
    // If no action is needed from the user.
    noAction: {
      type: Boolean,
      default: false,
    },
  },
  components: { EmailStatus, EmailRecipientsDialog },
  setup() {
    const applicationStore = useApplicationStore();
    const portcallStore = usePortcallStore();
    return {
      applicationStore,
      portcallStore,
    };
  },
  data() {
    return {
      showEmailStatusDialog: false,
      showRecipientsDialog: false,
      emailTemplateGuid: undefined,
      emailParams: undefined,
      emailTo: [],
      emailCc: [],
      emailBcc: [],
      sednaPopup: undefined,
    };
  },

  created() {},

  watch: {
    guid: {
      immediate: true,
      handler(val) {
        this.emailTemplateGuid = val;
        this.showEmailStatusDialog = val !== undefined;
      },
    },
  },

  computed: {
    askForRecipientsOnNewEmail() {
      return this.applicationStore.SGL_MAIL_ASK_ADDRESS === 1;
    },
    agencyId() {
      if (this.portcallId) {
        return this.portcallStore.callDetails.SETUP_ID;
      }
      return this.applicationStore.agencyIdSelected;
    },
    locationId() {
      let locId;
      switch (this.location) {
        case 'client':
          locId = this.clientId;
          break;
        case 'vessel':
          locId = this.vesselId;
          break;
        case 'proforma':
          locId = this.proformaId;
          break;
        default:
          locId = this.portcallId;
      }
      return locId;
    },
    isLoading: {
      get() {
        return false;
      },
      set(val) {
        this.$emit('isLoading', val);
      },
    },
    isLoadingOverlay: {
      get() {
        return false;
      },
      set(val) {
        this.$emit('isLoadingOverlay', val);
      },
    },
  },

  methods: {
    async createRecipientsArray(recipientsArray) {
      const error = [];
      const result = [];
      const promises = [];
      if (Array.isArray(recipientsArray) && recipientsArray.length > 0) {
        recipientsArray.forEach((item) => {
          if (isValidEmail(item.email)) {
            promises.push(
              apiGet(`portcall/emailRecipients/addManual/${item.email}`).then((res) => {
                if (Array.isArray(res) && res.length > 0) {
                  result.push(res[0]);
                } else {
                  result.push({ CLIENT_NAME: '', EMAIL: item.email, NAME: '' });
                }
              })
            );
          } else {
            error.push(`email ${item.email} is not a valid e-mail address`);
          }
        });
      }
      await Promise.allSettled(promises);
      return { error, result };
    },
    documentCreated(event) {
      this.$emit('documentCreated', event);
    },
    documentReady() {
      this.showEmailStatusDialog = true;
    },
    close() {
      this.showEmailStatusDialog = false;
    },
    async goToEmailClicked(emailId) {
      const sednaApi = new SednaApiService(this.agencyId);
      if (sednaApi.isSednaIntegration) {
        this.sednaPopup = sednaApi.reserveComposerPopup();
        if (this.sednaPopup?.blocked) return;
      }
      const email = await this.getEmail(emailId);
      if (email) {
        const promises = [];
        const toResult = await this.createRecipientsArray(email.TO);
        promises.push(toResult);
        const ccResult = await this.createRecipientsArray(email.CC);
        promises.push(ccResult);
        const bccResult = await this.createRecipientsArray(email.BCC);
        promises.push(bccResult);
        await Promise.allSettled(promises);
        const to = toResult.result;
        const cc = ccResult.result;
        const bcc = bccResult.result;
        const onLoadError = [...toResult.error, ...ccResult.error, ...bccResult.error];
        const content = email.MAIL_HTML;
        const subject = email.SUBJECT;
        const mailNo = email.MAIL_NO;
        const attachments = email.MAIL_ATTACHMENTS;
        const { location } = this.location;
        const params = {
          emailId,
          location,
          content,
          mailNo,
          subject,
          to,
          cc,
          bcc,
          attachments,
          onLoadError,
          isDraft: true,
          createdBy: email.SENT_BY,
          templateFrom: email.EMAIL_FROM,
          templateFromValue: email.EMAIL_FROM_VALUE,
          sednaCategoryId: email.DLA_SEDNA_CATEGORY_ID,
        };
        const useGlobalSettingForAskRecipient = email.ASK_TITLE === null; // If template, use value from template
        if ((useGlobalSettingForAskRecipient && this.askForRecipientsOnNewEmail) || email.ASK_TITLE) {
          this.sednaPopup?.close();
          this.sednaPopup = undefined;
          this.emailParams = params;
          this.emailTo = to;
          this.emailCc = cc;
          this.emailBcc = bcc;
          this.showRecipientsDialog = true;
        } else {
          await this.goToEmail(params);
        }
      } else {
        this.sednaPopup?.close();
        this.sednaPopup = undefined;
      }
    },
    async getEmail(emailId) {
      let result;
      try {
        result = await apiGet(`mail/${+emailId}`);
      } catch (error) {
        this.isLoadingOverlay = false;
        this.isLoading = false;
        console.error(error);
      }
      return result;
    },
    async getSednaJobIdForLocation() {
      const locationDetail = await getLocationDetail(this.locationId, this.location);
      return locationDetail?.SEDNA_JOB_ID;
    },
    async goToEmail(params) {
      this.isLoadingOverlay = true;
      const sednaApi = new SednaApiService(this.agencyId);
      try {
        if (sednaApi.isSednaIntegration) {
          const attachmentsFromDocuments = await getMailAttachments(params.emailId, params.attachments);
          let sednaJobId = await this.getSednaJobIdForLocation();
          if (!sednaJobId) {
            sednaJobId = await sednaApi.createSednaJobRef(this.locationId, this.location);
          }
          if (sednaJobId) {
            this.portcallStore.updateSednaJobId({ id: this.locationId, sednaJobId });
            const composeEmailResponse = await sednaApi.composeNewSednaEmail(
              sednaJobId,
              attachmentsFromDocuments,
              params
            );
            await apiDelete(`mail/${params.emailId}`);
            this.$emit('refreshEmail', null);
            sednaApi.openNewSednaEmailUrl(composeEmailResponse, this.sednaPopup, (result) => {
              if (result === 'sent' || result === 'discarded') this.$emit('refreshEmail', null);
            });
            this.sednaPopup = undefined;
          } else {
            this.sednaPopup?.close();
            this.sednaPopup = undefined;
          }
        } else if (this.location === 'client' && this.locationId) {
          this.$router.push({
            name: 'clientEmailNew',
            params: {
              ...params,
              clientId: this.locationId,
            },
          });
        } else if (this.location === 'proforma' && this.locationId) {
          this.$router.push({
            name: 'proformaEmailNew',
            params: {
              ...params,
              proformaId: this.locationId,
            },
          });
        } else if (
          (this.location === 'portcall' ||
            this.location === 'expense' ||
            this.location === 'service' ||
            this.location === 'sof' ||
            this.location === 'visitor' ||
            this.location === 'crewchange crew' ||
            this.location === 'proforma' ||
            this.location === 'crewchange task') &&
          this.locationId
        ) {
          this.$router.push({
            name: 'prortcallEmailNew',
            params: {
              ...params,
              portcallId: this.locationId,
            },
          });
        } else if (this.location === 'vessel' && this.locationId) {
          this.$router.push({
            name: 'vesselEmailNew',
            params: {
              ...params,
              vesselId: this.locationId,
            },
          });
        }
      } catch (error) {
        this.sednaPopup?.close();
        this.sednaPopup = undefined;
        throw error;
      } finally {
        this.isLoadingOverlay = false;
      }
    },

    recipientsCancelled() {
      this.sednaPopup?.close();
      this.sednaPopup = undefined;
      this.emailParams = undefined;
    },

    async recipientsSelected({ to, cc, bcc }) {
      this.showRecipientsDialog = false;
      if (this.emailParams) {
        const sednaApi = new SednaApiService(this.agencyId);
        if (sednaApi.isSednaIntegration) {
          this.sednaPopup = sednaApi.reserveComposerPopup();
          if (this.sednaPopup?.blocked) return;
        }
        const params = {
          ...this.emailParams,
          to,
          cc,
          bcc,
          saveDraftOnLoad: true,
        };
        this.params = undefined;
        this.emailTo = [];
        this.emailCc = [];
        this.emailBCc = [];
        await this.goToEmail(params);
      }
    },
  },
};
</script>

<style scoped></style>
